/**
 * SOLID Review
 * S — Strong. One use case: the new address's code — the address changed, then kept.
 * O — Strong. The round trip runs it; what is allowed is the Domain's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The decisions and auth-server ports and the round trip, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { ICommandEmailChanged, ICommandEmailCode, IPayloadEmailCode, IPayloadProfile } from '../../domain/ports/schemas/AuthMoves';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { TServerRoundTrip, TAnswered } from '../shared/ServerRoundTrip';
import type { IConfigDepAuth } from './IConfigDepAuth';

/** The profile's new address, confirmed by its code (FE3): changed on the server, then kept. */
export class VerifyEmailChangeUseCase implements IUseCase<ICommandEmailCode, TPortOutcome>, TServerRoundTrip<IPropsClientInput, ICommandEmailCode, IPayloadEmailCode, TAnswered<IPayloadProfile>> {
  constructor(private readonly deps: IConfigDepAuth) {}

  execute(ctx: IContextRequest, command: ICommandEmailCode): Promise<TPortOutcome> {
    return this.deps.trip.run(ctx, command, this);
  }

  plan(ctx: IContextRequest, input: IPropsClientInput, command: ICommandEmailCode) {
    return this.deps.decisions.planVerifyEmailChange(ctx, input, command);
  }

  send(ctx: IContextRequest, plan: IPayloadEmailCode) {
    return this.deps.auth.verifyEmailChange(ctx, plan);
  }

  answer(ctx: IContextRequest, input: IPropsClientInput, _plan: IPayloadEmailCode, saved: TAnswered<IPayloadProfile>) {
    const changed: ICommandEmailChanged = { email: saved.value.email };
    return this.deps.decisions.emailChanged(ctx, input, changed);
  }
}
